import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    city: '',
    address: '',
    street_number: '',
    fiscal_code: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  const loadReCaptchaV3 = (siteKey) =>
    new Promise((resolve, reject) => {
      if (window.grecaptcha && window.grecaptcha.execute) {
        resolve(window.grecaptcha);
        return;
      }

      const existing = document.getElementById('recaptcha-v3');
      if (!existing) {
        const script = document.createElement('script');
        script.id = 'recaptcha-v3';
        script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
        script.async = true;
        script.defer = true;
        script.onerror = () => reject(new Error('Impossibile caricare reCAPTCHA.'));
        document.body.appendChild(script);
      }

      const startedAt = Date.now();
      const timer = window.setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.execute) {
          window.clearInterval(timer);
          resolve(window.grecaptcha);
          return;
        }
        if (Date.now() - startedAt > 6000) {
          window.clearInterval(timer);
          reject(new Error('Timeout caricamento reCAPTCHA.'));
        }
      }, 100);
    });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      let token;
      if (recaptchaSiteKey) {
        const grecaptcha = await loadReCaptchaV3(recaptchaSiteKey);
        token = await grecaptcha.execute(recaptchaSiteKey, { action: 'register' });
      }

      const result = await register(formData, token);
      
      if (result.success) {
        setSuccess(true);
        // Reset form
        setFormData({
          email: '',
          first_name: '',
          last_name: '',
          phone_number: '',
          city: '',
          address: '',
          street_number: '',
          fiscal_code: ''
        });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Si è verificato un errore durante la registrazione');
      console.error(err);
    }
    
    setLoading(false);
  };

  return (
    <div className="mx-auto mt-8 w-full max-w-2xl">
      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Registrazione</h1>
              <p className="mt-1 text-sm text-slate-600">Richiedi l&apos;accesso alla Banca del Tempo</p>
            </div>
            <img src="/Logo.png" alt="Logo" className="h-11 w-11 rounded-xl" />
          </div>

          {error ? <div className="alert-error mt-4">{error}</div> : null}

          {success ? (
            <div className="alert-success mt-4">
              Richiesta di registrazione inviata con successo! Un amministratore o moderatore approverà la tua richiesta. Riceverai le credenziali via email.
              <div className="mt-3">
                <button type="button" className="btn-outline" onClick={() => navigate('/login')}>
                  Vai al login
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="email" className="label">
                    Indirizzo Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    autoFocus
                    value={formData.email}
                    onChange={handleChange}
                    className="input mt-1"
                  />
                </div>

                <div>
                  <label htmlFor="first_name" className="label">
                    Nome
                  </label>
                  <input
                    id="first_name"
                    name="first_name"
                    autoComplete="given-name"
                    required
                    value={formData.first_name}
                    onChange={handleChange}
                    className="input mt-1"
                  />
                </div>

                <div>
                  <label htmlFor="last_name" className="label">
                    Cognome
                  </label>
                  <input
                    id="last_name"
                    name="last_name"
                    autoComplete="family-name"
                    required
                    value={formData.last_name}
                    onChange={handleChange}
                    className="input mt-1"
                  />
                </div>

                <div>
                  <label htmlFor="phone_number" className="label">
                    Cellulare
                  </label>
                  <input
                    id="phone_number"
                    name="phone_number"
                    autoComplete="tel"
                    required
                    value={formData.phone_number}
                    onChange={handleChange}
                    className="input mt-1"
                  />
                </div>

                <div>
                  <label htmlFor="city" className="label">
                    Città
                  </label>
                  <input
                    id="city"
                    name="city"
                    autoComplete="address-level2"
                    required
                    value={formData.city}
                    onChange={handleChange}
                    className="input mt-1"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="address" className="label">
                    Indirizzo
                  </label>
                  <input
                    id="address"
                    name="address"
                    autoComplete="address-line1"
                    required
                    value={formData.address}
                    onChange={handleChange}
                    className="input mt-1"
                  />
                </div>

                <div>
                  <label htmlFor="street_number" className="label">
                    Numero Civico
                  </label>
                  <input
                    id="street_number"
                    name="street_number"
                    autoComplete="address-line2"
                    required
                    value={formData.street_number}
                    onChange={handleChange}
                    className="input mt-1"
                  />
                </div>

                <div>
                  <label htmlFor="fiscal_code" className="label">
                    Codice Fiscale
                  </label>
                  <input
                    id="fiscal_code"
                    name="fiscal_code"
                    required
                    value={formData.fiscal_code}
                    onChange={handleChange}
                    className="input mt-1"
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <button type="button" onClick={() => navigate('/login')} className="btn-ghost">
                  Hai già un account? Accedi
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Invio in corso...' : 'Registrati'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
